import { cur } from '@/lib/currency';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  GitCompareArrows, Zap, Loader2, RefreshCw, CheckCircle2, XCircle,
  ArrowRight, Inbox, Building2, FileText, Receipt, Link2, ArrowUpRight,
  Sparkles, Users, Truck, UserCircle, Tag, Clock, Settings2, Info,
} from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import { BranchFilter } from '@/components/filters/BranchFilter';
import { TxnActionDrawer } from '@/components/banking/TxnActionDrawer';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function fmt(v) { return (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function ReconciliationCenterPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState('012-2');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [windowDays, setWindowDays] = useState(5);
  // iter — universal branch filter + inline transaction drawer (P0)
  const [viewBranchId, setViewBranchId] = useState(null);
  const [drawerTxn, setDrawerTxn] = useState(null);
  const [reviewLaterKeys, setReviewLaterKeys] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  // iter946 — cross-branch internal-offset suggestions from multi-signal auto-match
  const [crossBranch, setCrossBranch] = useState([]);
  const [lastReconId, setLastReconId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  // ── Recurring patterns (iter390) ─────────────────────────────
  const [patterns, setPatterns] = useState([]);
  const [patternsLoading, setPatternsLoading] = useState(false);
  const [showPatterns, setShowPatterns] = useState(false);

  const loadPatterns = async () => {
    setPatternsLoading(true);
    try {
      const r = await api.get('/accounting/reconciliation/recurring-patterns', {
        params: { days: 90, min_occurrences: 2 },
      });
      setPatterns(r.data?.patterns || []);
    } catch { /* ignore — non-blocking */ }
    setPatternsLoading(false);
  };

  useEffect(() => { loadPatterns(); }, []);

  // ── Smart bulk-create state — multi-select bank rows + group → entity link
  const [selectedBankIds, setSelectedBankIds] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkEntityType, setBulkEntityType] = useState('supplier');
  const [bulkEntityId, setBulkEntityId] = useState('');
  const [bulkEntityList, setBulkEntityList] = useState([]);
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Load all bank/cash COA accounts to populate the selector
  const loadAccounts = async () => {
    try {
      const r = await api.get('/accounting/accounts');
      const cashish = (r.data || []).filter(a =>
        a.type === 'asset' && (
          /bank|cash/i.test(a.name) ||
          /^(012|1010|1000|1100)/.test(a.code || '')
        ),
      );
      setAccounts(cashish);
      // iter395 — Auto-pick the first cash/bank account so users
      // landing on the page with no `012-2` configured don't see an
      // empty list. Only set if user hasn't picked one yet.
      if (cashish.length > 0 && account === '012-2' && !cashish.some(a => a.code === '012-2')) {
        setAccount(cashish[0].code);
      }
    } catch { /* non-fatal */ }
  };

  const loadCenter = async () => {
    setLoading(true);
    try {
      const params = { account, limit: 100 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (viewBranchId) params.branch = viewBranchId;
      const r = await api.get('/accounting/reconciliation/center', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reconciliation center');
    }
    setLoading(false);
  };

  useEffect(() => { loadAccounts(); }, []);
  useEffect(() => { loadCenter(); /* eslint-disable-next-line */ }, [account, dateFrom, dateTo, viewBranchId]);

  const runAutoMatch = async () => {
    setRunning(true);
    try {
      const res = await api.post('/accounting/reconciliation/auto-match', {
        account,
        window_days: Number(windowDays) || 5,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      const d = res.data || {};
      const c = d.match_count || 0;
      const rid = d.reconciliation_id || null;
      const xb = (d.cross_branch_suggestions || []).map(s => ({ ...s, reconciliation_id: rid }));
      setCrossBranch(xb);
      setLastReconId(rid);
      if (c > 0) {
        toast.success(`Matched ${c} entries · scanned ${d.je_candidates_scanned} JEs / ${d.bank_txn_candidates_scanned} bank txns${xb.length ? ` · ${xb.length} cross-branch to approve` : ''}`);
      } else {
        toast(`No matches — try widening the window. Scanned ${d.je_candidates_scanned} JEs / ${d.bank_txn_candidates_scanned} bank txns.`);
      }
      loadCenter();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-match failed');
    }
    setRunning(false);
  };

  // Load tenant-wide persisted pending offsets on mount so approvals survive a
  // page refresh and can be cleared in bulk without re-running auto-match.
  const loadPendingOffsets = async () => {
    try {
      const r = await api.get('/accounting/reconciliation/pending-offsets');
      const list = r.data?.suggestions || [];
      if (list.length) {
        setCrossBranch(prev => (prev.length ? prev : list));
        if (!lastReconId && list[0]?.reconciliation_id) setLastReconId(list[0].reconciliation_id);
      }
    } catch { /* non-blocking */ }
  };
  useEffect(() => { loadPendingOffsets(); /* eslint-disable-next-line */ }, []);

  const approveOffset = async (s) => {
    const rid = s.reconciliation_id || lastReconId;
    if (!rid) { toast.error('Run auto-match first'); return; }
    setApprovingId(s.entry_id);
    try {
      const res = await api.post('/accounting/reconciliation/approve-offset', {
        reconciliation_id: rid,
        entry_id: s.entry_id,
        from_branch_id: s.from_branch_id,
        to_branch_id: s.to_branch_id,
        amount: s.amount,
        date: s.date,
        notes: s.description,
      });
      toast.success(res.data?.settlement?.message || 'Internal offset created');
      setCrossBranch(prev => prev.map(x => x.entry_id === s.entry_id ? { ...x, approved: true } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
      throw e;
    }
    setApprovingId(null);
  };

  const [approvingAll, setApprovingAll] = useState(false);
  const approveAllOffsets = async () => {
    const pending = crossBranch.filter(s => !s.approved);
    if (!pending.length) return;
    setApprovingAll(true);
    let ok = 0;
    for (const s of pending) {
      try { await approveOffset(s); ok += 1; } catch { /* keep going */ }
    }
    setApprovingId(null);
    setApprovingAll(false);
    toast.success(`Approved ${ok} of ${pending.length} cross-branch offset${pending.length === 1 ? '' : 's'}`);
  };

  const approveRun = async (recon) => {
    try {
      await api.post('/accounting/reconciliation/approve-run', { reconciliation_id: recon.id });
      toast.success('Run approved & locked');
      loadCenter();
    } catch { toast.error('Approve failed'); }
  };

  const rejectRun = async (recon) => {
    if (!window.confirm(`Undo all ${recon.still_reconciled} entries from this run?`)) return;
    try {
      await api.post('/accounting/reconciliation/unmatch', { entry_ids: recon.entry_ids });
      toast.success('Run reverted — entries are unreconciled again');
      loadCenter();
    } catch { toast.error('Undo failed'); }
  };

  const summary = data?.summary || {};
  const jeRows = data?.unmatched_je || [];
  const bankRows = data?.unmatched_bank || [];
  const pending = data?.pending_auto_matches || [];
  // iter — resolve branch_id → readable name for the Review drawer (display
  // only; does NOT change branch allocation or permissions). Reuses the
  // global branchStore (same source as the Branch filter dropdown).
  const branches = useBranchStore((s) => s.branches);
  const branchNameById = useMemo(() => {
    const m = {};
    (branches || []).forEach((b) => { m[b.id] = b.name; });
    return m;
  }, [branches]);
  const drawerTxnResolved = useMemo(() => {
    if (!drawerTxn) return null;
    return { ...drawerTxn, branch_name: branchNameById[drawerTxn.branch_id] || drawerTxn.branch_name };
  }, [drawerTxn, branchNameById]);
  // Quick-Link the txn — pre-fill the URL so BankingQuickLinkPage can focus the row
  const openInQuickLink = (row) => {
    navigate(`/banking-quick-link?statement_id=${row.statement_id}&txn_index=${row.txn_index}`);
  };

  // Open the JE in the Journal Entries page filtered to this entry number
  const openJournalEntry = (row) => {
    navigate(`/journal-entries?entry_number=${encodeURIComponent(row.entry_number || '')}`);
  };

  // ── Inline transaction drawer handlers (P0) ───────────────────
  // Posting actions (Add Payment / Add Expense) REUSE the page's existing,
  // validated entity-link dialog (which posts to bulk-link-to-entity) with
  // this single txn preselected — the dialog is the confirmation step. The
  // remaining actions deep-link to their existing validated pages.
  const drawerAddPayment = (txn) => {
    setDrawerTxn(null);
    setSelectedBankIds(new Set([bankRowKey(txn)]));
    setBulkEntityType('supplier');
    setBulkEntityId('');
    setBulkSearch('');
    setBulkOpen(true);
    loadEntityList('supplier');
  };
  const drawerAddExpense = (txn) => {
    setDrawerTxn(null);
    setSelectedBankIds(new Set([bankRowKey(txn)]));
    setBulkEntityType('expense_category');
    setBulkEntityId('');
    setBulkSearch('');
    setBulkOpen(true);
    loadEntityList('expense_category');
  };
  const drawerLinkExisting = (txn) => { setDrawerTxn(null); openInQuickLink(txn); };
  const drawerTransfer = (txn) => {
    setDrawerTxn(null);
    navigate(`/banking-hub?focus_statement=${txn.statement_id}&focus_txn=${txn.txn_index}&action=transfer`);
  };
  const drawerPassThrough = (txn) => {
    setDrawerTxn(null);
    navigate(`/banking-hub?focus_statement=${txn.statement_id}&focus_txn=${txn.txn_index}&action=passthrough`);
  };
  const drawerReviewLater = (txn) => {
    setReviewLaterKeys(prev => new Set(prev).add(bankRowKey(txn)));
    setDrawerTxn(null);
    toast.info('Hidden for now — it stays unmatched and will return on reload.');
  };

  // ── Smart bulk-create ─────────────────────────────────────────
  const bankRowKey = (r) => `${r.statement_id}::${r.txn_index}`;
  // Rows the user chose to "Review later" are hidden this session only.
  const visibleBankRows = bankRows.filter(r => !reviewLaterKeys.has(bankRowKey(r)));
  const toggleBankRow = (r) => {
    const k = bankRowKey(r);
    setSelectedBankIds(prev => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };

  // Group selected rows by approximate description (first 3 words). User can
  // see this in the dialog so it's clear which rows are headed to which entity.
  const selectedBankRows = useMemo(() =>
    bankRows.filter(r => selectedBankIds.has(bankRowKey(r))),
    [bankRows, selectedBankIds]);

  const selectedSummary = useMemo(() => {
    const total = selectedBankRows.reduce((s, r) => s + Math.abs(r.amount || 0), 0);
    return { count: selectedBankRows.length, total };
  }, [selectedBankRows]);

  // Group by normalized name (first non-numeric chunk of description) for the
  // "auto-group" hint shown in the dialog header.
  const groups = useMemo(() => {
    const out = {};
    for (const r of selectedBankRows) {
      const key = (r.description || '—').split(/[/\s]+/).slice(0, 3).join(' ').toLowerCase();
      if (!out[key]) out[key] = { sample: r.description, rows: [], total: 0 };
      out[key].rows.push(r);
      out[key].total += Math.abs(r.amount || 0);
    }
    return Object.entries(out).map(([k, v]) => ({ key: k, ...v }));
  }, [selectedBankRows]);

  const ENTITY_TYPES = [
    { key: 'supplier', label: 'Supplier', icon: Truck, endpoint: '/suppliers' },
    { key: 'customer', label: 'Customer', icon: Users, endpoint: '/customers' },
    { key: 'employee', label: 'Employee', icon: UserCircle, endpoint: '/employees' },
    { key: 'partner', label: 'Partner', icon: Users, endpoint: '/partners' },
    { key: 'expense_category', label: 'Expense Category', icon: Tag, endpoint: '/categories?type=expense' },
  ];

  const [autoCreateExpense, setAutoCreateExpense] = useState(true);
  const [bulkPattern, setBulkPattern] = useState('');

  const openBulkDialog = async () => {
    if (selectedBankIds.size < 1) { toast.error('Select at least 1 bank txn first'); return; }
    setBulkOpen(true);
    setBulkEntityId('');
    setBulkSearch('');
    await loadEntityList(bulkEntityType);
  };

  const loadEntityList = async (type) => {
    const cfg = ENTITY_TYPES.find(e => e.key === type);
    if (!cfg?.endpoint) { setBulkEntityList([]); return; }
    setBulkLoading(true);
    try {
      const r = await api.get(cfg.endpoint);
      const list = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      setBulkEntityList(list.filter(x => x?.id).map(x => ({
        id: x.id,
        name: x.name || x.full_name || '—',
        sub: x.phone || x.email || x.tax_number || '',
      })));
    } catch { toast.error(`Could not load ${type}s`); }
    setBulkLoading(false);
  };

  useEffect(() => { if (bulkOpen) loadEntityList(bulkEntityType); /* eslint-disable-next-line */ }, [bulkEntityType]);

  const filteredBulkEntities = useMemo(() =>
    bulkSearch
      ? bulkEntityList.filter(o => (o.name || '').toLowerCase().includes(bulkSearch.toLowerCase()))
      : bulkEntityList,
    [bulkEntityList, bulkSearch]);

  const submitBulkLink = async () => {
    if (!bulkEntityId && bulkEntityType !== 'expense_category') {
      toast.error('Pick an entity first'); return;
    }
    if (bulkEntityType === 'expense_category' && !bulkEntityId) {
      toast.error('Pick a category first'); return;
    }
    setBulkSubmitting(true);
    try {
      // Find selected category name (for auto-create payload)
      const catName = bulkEntityList.find(e => e.id === bulkEntityId)?.name || '';
      const res = await api.post('/banking/statement-txn/bulk-link-to-entity', {
        txns: selectedBankRows.map(r => ({ statement_id: r.statement_id, txn_index: r.txn_index })),
        entity_type: bulkEntityType,
        entity_id: bulkEntityId || undefined,
        auto_create_expense: bulkEntityType === 'expense_category' ? autoCreateExpense : false,
        expense_category_name: bulkEntityType === 'expense_category' ? catName : undefined,
        expense_pattern: bulkPattern || (groups[0]?.sample || '').slice(0, 60),
      });
      const d = res.data || {};
      const ok = d.linked || 0;
      const fail = d.failed_count || 0;
      const expN = d.expenses_created || 0;
      if (ok > 0 && fail === 0) {
        toast.success(`Linked ${ok} bank txn(s)${expN ? ` · created ${expN} expense(s)` : ''}`);
      } else if (ok > 0 && fail > 0) {
        toast(`Linked ${ok} · ${fail} failed${expN ? ` · ${expN} expense(s) created` : ''}`);
      } else {
        toast.error(`All ${fail} link(s) failed — see console`);
      }
      if (fail) console.warn('Bulk-link failures:', d.failed);
      setBulkOpen(false);
      setSelectedBankIds(new Set());
      setBulkPattern('');
      loadCenter();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk link failed');
    }
    setBulkSubmitting(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-[1500px] mx-auto" data-testid="reconciliation-center-page">
        <PageInfoCallout
          storageKey="reconciliation-center"
          title="What is the Reconciliation Center?"
          defaultOpen={false}
        >
          <p>
            One screen for closing the books. Left panel shows your <strong>journal entries</strong> on this account waiting to be reconciled. Middle panel shows <strong>bank-statement transactions</strong> not yet linked to anything in Smarto. Right panel shows recent <strong>auto-match runs</strong> that need manager sign-off. Click <strong>Auto-Match</strong> to pair JEs ↔ Bank Txns by amount inside ±N days, then approve or undo.
          </p>
        </PageInfoCallout>

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-200/50">
              <GitCompareArrows size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Banking Reconciliation Inbox</h1>
              <p className="text-sm text-muted-foreground mt-0.5">One inbox to close the month — match journal entries to bank in seconds.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadCenter} disabled={loading} data-testid="refresh-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </Button>
            <Button
              size="sm"
              onClick={runAutoMatch}
              disabled={running || !account}
              className="bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white"
              data-testid="auto-match-btn"
            >
              {running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Zap size={14} className="mr-1" />}
              Auto-Match (±{windowDays}d)
            </Button>
          </div>
        </div>

        {/* iter — Advanced tools in ONE compact menu so everyday users
            aren't overwhelmed. The overlapping recon pages stay reachable
            here (and by direct URL) — reused, not rebuilt. */}
        <div className="flex items-center gap-2" data-testid="recon-advanced-tools">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs rounded-lg" data-testid="recon-advanced-menu-trigger">
                <Settings2 size={13} className="mr-1.5" /> Advanced tools
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/platform-reconciliation')} data-testid="adv-platform-settlements">
                <ArrowUpRight size={13} className="mr-2" /> Platform Settlements
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/banking-hub')} data-testid="adv-fees-vat">
                <ArrowUpRight size={13} className="mr-2" /> Bank Fees &amp; VAT
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/monthly-reconciliation')} data-testid="adv-monthly-tracker">
                <ArrowUpRight size={13} className="mr-2" /> Monthly Tracker
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/smart-reconciliation')} data-testid="adv-smart-match">
                <ArrowUpRight size={13} className="mr-2" /> Smart Match
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/recon-cockpit')} data-testid="adv-cockpit">
                <ArrowUpRight size={13} className="mr-2" /> Recon Cockpit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Account</label>
              <Select value={account} onValueChange={setAccount}>
                <SelectTrigger className="h-9 w-[260px] text-xs" data-testid="account-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accounts.length === 0 && <SelectItem value="012-2">012-2 — Bank Account (Main)</SelectItem>}
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.code} className="text-xs">{a.code} — {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div data-testid="recon-branch-wrap">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Branch</label>
              <BranchFilter value={viewBranchId} onChange={setViewBranchId} width="w-44" testid="recon-branch" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">From</label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-40 text-xs" data-testid="date-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">To</label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-40 text-xs" data-testid="date-to" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Match window</label>
              <div className="flex items-center gap-1">
                <Input
                  type="number" min={1} max={30} value={windowDays}
                  onChange={e => setWindowDays(e.target.value)}
                  className="h-9 w-16 text-xs"
                  data-testid="window-days"
                />
                <span className="text-xs text-muted-foreground">days</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status segments — the approved reconciliation statuses, built from
            existing /center data. Counts shown are AUTHORITATIVE TOTALS
            (server-side $count, not the paginated row length). "Matched ·
            Suggested" (auto-matched, awaiting approval) is explicitly
            distinct from the confirmed "Reconciled" count. Each card carries
            an info tooltip explaining its exact scope. "Possible Duplicate"
            is detected per transaction inside the Review drawer. */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="summary-strip">
          <Card className="border-warning/30 bg-warning/5" data-testid="seg-unmatched-bank">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-warning font-semibold flex items-center gap-1">
                Needs Review · Bank
                <Info size={11} className="text-warning/60 shrink-0" data-testid="tip-bank"
                  title="Total unmatched bank-statement transactions in the selected date range and branch scope. Bank statements are not tied to the chosen account — this count is bank-wide. The list below shows the first page only." />
              </p>
              <p className="text-2xl font-bold text-warning mt-0.5" data-testid="bank-count">{summary.bank_total ?? summary.bank_count ?? 0}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                unmatched bank txns to action
                {(summary.bank_total ?? 0) > (summary.bank_count ?? 0) && (
                  <span className="block" data-testid="bank-paged-note">showing first {summary.bank_count} below</span>
                )}
              </p>
            </CardContent>
          </Card>
          <Card className="border-info/30 bg-info/5" data-testid="seg-unmatched-je">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-info font-semibold flex items-center gap-1">
                Unmatched · JEs
                <Info size={11} className="text-info/60 shrink-0" data-testid="tip-je"
                  title="Total unreconciled journal-entry lines on the selected account, date range and branch scope. The list below shows the first page only." />
              </p>
              <p className="text-2xl font-bold text-info mt-0.5" data-testid="je-count">{summary.je_total ?? summary.je_count ?? 0}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                journal lines awaiting a bank match
                {(summary.je_total ?? 0) > (summary.je_count ?? 0) && (
                  <span className="block" data-testid="je-paged-note">showing first {summary.je_count} below</span>
                )}
              </p>
            </CardContent>
          </Card>
          <Card className="border-violet-200 bg-violet-50/40" data-testid="seg-suggested">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold flex items-center gap-1">
                Matched · Suggested
                <Info size={11} className="text-violet-700/60 shrink-0" data-testid="tip-suggested"
                  title="Auto-match SUGGESTIONS awaiting manager approval — NOT yet confirmed. Counted from the most recent auto-match runs across the company (not scoped to the selected account/date). Approve a run to turn these into Reconciled." />
              </p>
              <p className="text-2xl font-bold text-violet-700 mt-0.5" data-testid="pending-entries">{summary.pending_entries_total || 0}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">suggested — needs approval ({summary.pending_runs || 0} run{(summary.pending_runs || 0) === 1 ? '' : 's'})</p>
            </CardContent>
          </Card>
          <Card className="border-amber-300 bg-amber-50/40" data-testid="seg-possible-duplicate">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold flex items-center gap-1">
                Possible Duplicate
                <Info size={11} className="text-amber-700/60 shrink-0" data-testid="tip-dup"
                  title="Duplicates are detected per transaction when you open 'Review & act' — the drawer runs a read-only scan and warns before you create a new record. This is a guide, not a bucket of counted rows." />
              </p>
              <p className="text-base font-bold text-amber-700 mt-1.5" data-testid="dup-note">Per transaction</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">checked in “Review &amp; act”</p>
            </CardContent>
          </Card>
          <Card className="border-success/30 bg-success/5" data-testid="seg-reconciled">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-success font-semibold flex items-center gap-1">
                Reconciled
                <Info size={11} className="text-success/60 shrink-0" data-testid="tip-reconciled"
                  title="CONFIRMED reconciled bank transactions in the selected date range and branch scope (linked / matched / reconciled). These are final, unlike 'Matched · Suggested'." />
              </p>
              <p className="text-2xl font-bold text-success mt-0.5" data-testid="reconciled-count">{summary.reconciled_count ?? 0}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">confirmed in this period</p>
            </CardContent>
          </Card>
        </div>

        {/* iter395 — Empty-state guidance. When the user has zero JEs,
            zero bank txns AND zero pending runs on the selected account
            we show a friendly card telling them what to do next instead
            of leaving them staring at three empty panels. Hidden as soon
            as anything appears. */}
        {!loading && (summary.je_count || 0) === 0 && (summary.bank_count || 0) === 0 && (summary.pending_runs || 0) === 0 && (
          <Card className="border-dashed border-violet-300 bg-violet-50/30" data-testid="reconcil-empty-state">
            <CardContent className="p-5 flex flex-wrap items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center shrink-0">
                <Inbox size={22} className="text-violet-700" />
              </div>
              <div className="flex-1 min-w-[260px]">
                <h3 className="text-sm font-semibold text-violet-900">Nothing to reconcile yet on <code className="bg-white px-1.5 py-0.5 rounded text-[11px]">{account}</code></h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Reconciliation works by matching <strong>journal entries</strong> on this account against <strong>bank-statement transactions</strong>.
                  If both panels are empty, you probably haven&apos;t uploaded a bank statement yet, or the selected account has no postings in the date range.
                </p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <Button size="sm" variant="outline" onClick={() => navigate('/bank-statements')} data-testid="reconcil-empty-upload">
                  <ArrowUpRight size={12} className="mr-1.5" /> Upload bank statement
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate('/journal-entries')} data-testid="reconcil-empty-je">
                  <FileText size={12} className="mr-1.5" /> View journal entries
                </Button>
                <Button size="sm" variant="ghost" onClick={() => navigate('/banking-quick-link')} data-testid="reconcil-empty-quicklink" className="text-violet-700">
                  <Link2 size={12} className="mr-1.5" /> Quick-link inbox
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recurring Pattern Detector banner (iter390) */}
        {showPatterns && (patterns.length > 0 || patternsLoading) && (
          <Card className="border-violet-200 bg-gradient-to-r from-violet-50/60 via-fuchsia-50/40 to-pink-50/30" data-testid="recurring-patterns-card">
            <div className="px-4 py-3 border-b border-violet-200 flex items-center gap-2 flex-wrap">
              <Sparkles size={15} className="text-violet-600" />
              <h2 className="text-sm font-semibold text-violet-700">Recurring patterns (last 90 days)</h2>
              <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">{patterns.length}</Badge>
              <Button size="sm" variant="ghost" className="ml-auto h-7 text-[10px] gap-1" onClick={loadPatterns} disabled={patternsLoading} data-testid="refresh-patterns-btn">
                {patternsLoading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Re-scan
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setShowPatterns(false)} data-testid="dismiss-patterns-btn">Hide</Button>
            </div>
            <CardContent className="p-3">
              {patternsLoading ? (
                <p className="text-xs text-muted-foreground text-center py-3"><Loader2 size={12} className="inline animate-spin mr-1" /> Scanning bank statements…</p>
              ) : patterns.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">No repeating patterns detected — your bank txns look mostly one-off.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 max-h-[260px] overflow-y-auto">
                  {patterns.slice(0, 12).map((p, i) => (
                    <div key={`${p.narrative}-${p.amount}-${i}`} className="rounded-lg bg-card border border-violet-100 p-2.5 hover:border-violet-300 transition-colors" data-testid={`pattern-card-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                          p.cadence === 'monthly' ? 'bg-violet-100 text-violet-700' :
                          p.cadence === 'weekly' || p.cadence === 'bi-weekly' ? 'bg-pink-100 text-pink-700' :
                          p.cadence === 'quarterly' ? 'bg-blue-100 text-blue-700' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {p.cadence}
                        </span>
                        <span className={`text-[10px] font-semibold ${p.sign === 'out' ? 'text-error' : 'text-success'}`}>
                          {p.sign === 'out' ? '↑ Outgoing' : '↓ Incoming'}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-foreground mt-1.5 truncate" title={p.sample_description}>
                        {p.narrative}
                      </p>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="font-mono text-xs text-foreground">{cur()} {fmt(p.amount)} × {p.occurrences}</span>
                        <span className="text-[10px] text-muted-foreground">≈{p.median_gap_days}d apart</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2">
                        {p.already_recurring ? (
                          <Badge variant="outline" className="text-[9px] bg-success/10 text-success border-success/30">
                            <CheckCircle2 size={9} className="mr-0.5" /> Already recurring
                          </Badge>
                        ) : p.sign === 'out' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] gap-1 border-violet-200 text-violet-700"
                            onClick={() => navigate('/recurring-entries?prefill_pattern=' + encodeURIComponent(p.narrative) + '&amount=' + p.amount)}
                            data-testid={`setup-recurring-${i}`}
                          >
                            <ArrowRight size={9} /> Set up as recurring
                          </Button>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">Incoming — review manually</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 3-Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Panel 1 — Unmatched JEs */}
          <Card className="overflow-hidden" data-testid="panel-unmatched-je">
            <div className="px-4 py-3 bg-info/10 border-b border-info/20 flex items-center gap-2">
              <FileText size={15} className="text-info" />
              <h2 className="text-sm font-semibold text-info">Unmatched Journal Entries</h2>
              <Badge variant="outline" className="ml-auto text-[10px] bg-info/10 text-info border-info/30">{jeRows.length}</Badge>
            </div>
            <CardContent className="p-0 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-muted-foreground text-xs"><Loader2 size={14} className="inline animate-spin mr-1" /> Loading…</div>
              ) : jeRows.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-xs">
                  <CheckCircle2 size={28} className="mx-auto mb-2 text-success/40" />
                  All journal entries on this account are reconciled.
                </div>
              ) : (
                <div className="divide-y divide-border/40">
                  {jeRows.map((r, i) => (
                    <div key={`${r.entry_id}-${i}`} className="p-3 hover:bg-info/5 cursor-pointer" onClick={() => openJournalEntry(r)} data-testid={`je-row-${r.entry_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[11px] text-muted-foreground">{r.entry_number}</span>
                        <span className="text-[11px] text-muted-foreground">{r.date}</span>
                      </div>
                      <p className="text-sm font-medium text-foreground mt-1 truncate" title={r.description}>{r.description || '—'}</p>
                      <div className="flex items-center justify-between gap-2 mt-1.5">
                        <div className="flex items-center gap-3 text-xs">
                          {r.debit > 0 && <span className="text-success font-mono">Dr {fmt(r.debit)}</span>}
                          {r.credit > 0 && <span className="text-info font-mono">Cr {fmt(r.credit)}</span>}
                        </div>
                        <ArrowUpRight size={13} className="text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Panel 2 — Unmatched Bank Txns (ordered FIRST so the day-to-day
              bank work sits in the primary reading position) */}
          <Card className="overflow-hidden order-first lg:order-first" data-testid="panel-unmatched-bank">
            <div className="px-4 py-3 bg-warning/10 border-b border-warning/20 flex items-center gap-2">
              <Building2 size={15} className="text-warning" />
              <h2 className="text-sm font-semibold text-warning">Unmatched Bank Txns</h2>
              <Badge variant="outline" className="ml-auto text-[10px] bg-warning/10 text-warning border-warning/30">{visibleBankRows.length}</Badge>
            </div>
            {selectedBankIds.size > 0 && (
              <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2 text-xs" data-testid="bulk-action-bar">
                <span className="text-emerald-700 font-semibold">{selectedSummary.count} selected · {cur()} {fmt(selectedSummary.total)}</span>
                <Button size="sm" className="ml-auto h-7 text-[11px] gap-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
                  onClick={openBulkDialog} data-testid="bulk-create-btn">
                  <Sparkles size={11} /> Smart bulk-link
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setSelectedBankIds(new Set())} data-testid="bulk-clear-btn">Clear</Button>
              </div>
            )}
            <CardContent className="p-0 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-muted-foreground text-xs"><Loader2 size={14} className="inline animate-spin mr-1" /> Loading…</div>
              ) : bankRows.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-xs">
                  <Inbox size={28} className="mx-auto mb-2 opacity-40" />
                  No unmatched bank transactions in this period.
                </div>
              ) : visibleBankRows.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-xs">
                  <Clock size={28} className="mx-auto mb-2 opacity-40" />
                  All remaining txns are hidden under “Review later”. Reload to bring them back.
                </div>
              ) : (
                <div className="divide-y divide-border/40">
                  {visibleBankRows.map((r, i) => {
                    const k = bankRowKey(r);
                    const checked = selectedBankIds.has(k);
                    return (
                    <div key={`${r.statement_id}-${r.txn_index}-${i}`} className={`p-3 hover:bg-warning/5 ${checked ? 'bg-emerald-50/50' : ''}`} data-testid={`bank-row-${r.statement_id}-${r.txn_index}`}>
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleBankRow(r)}
                          className="mt-0.5 shrink-0"
                          data-testid={`bank-row-checkbox-${r.statement_id}-${r.txn_index}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono text-[11px] text-muted-foreground truncate">{r.bank_name || 'Bank'} · {r.reference || `#${r.txn_index}`}</span>
                            <span className="text-[11px] text-muted-foreground">{r.date}</span>
                          </div>
                          <p className="text-sm font-medium text-foreground mt-1 truncate" title={r.description}>{r.description || '—'}</p>
                          <div className="flex items-center justify-between gap-2 mt-1.5">
                            <span className={`font-mono text-xs font-semibold ${r.amount >= 0 ? 'text-success' : 'text-error'}`}>
                              {cur()} {fmt(Math.abs(r.amount))} {r.amount >= 0 ? '↓' : '↑'}
                            </span>
                            <Button size="sm" variant="ghost" className="h-7 text-[10px] gap-1 text-primary"
                              onClick={() => setDrawerTxn(r)}
                              data-testid={`review-txn-${r.statement_id}-${r.txn_index}`}
                            >
                              <ArrowUpRight size={11} /> Review & act
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )})}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Panel 3 — Pending Auto-Match Runs */}
          <Card className="overflow-hidden" data-testid="panel-pending-matches">
            <div className="px-4 py-3 bg-violet-100/60 border-b border-violet-200 flex items-center gap-2">
              <Zap size={15} className="text-violet-700" />
              <h2 className="text-sm font-semibold text-violet-700">Pending Auto-Match Runs</h2>
              <Badge variant="outline" className="ml-auto text-[10px] bg-violet-50 text-violet-700 border-violet-200">{pending.length}</Badge>
            </div>
            <CardContent className="p-0 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-muted-foreground text-xs"><Loader2 size={14} className="inline animate-spin mr-1" /> Loading…</div>
              ) : pending.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-xs">
                  <Receipt size={28} className="mx-auto mb-2 opacity-40" />
                  No auto-match runs yet. Click "Auto-Match (±{windowDays}d)" above to start.
                </div>
              ) : (
                <div className="divide-y divide-border/40">
                  {pending.map(r => (
                    <div key={r.id} className="p-3" data-testid={`pending-${r.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[11px] text-muted-foreground truncate">{r.account || '—'}</span>
                        <span className="text-[11px] text-muted-foreground">{(r.created_at || '').slice(0, 10)}</span>
                      </div>
                      <p className="text-sm font-medium text-foreground mt-1">
                        {r.still_reconciled} matched
                        {r.rejected_count > 0 && <span className="text-muted-foreground"> · {r.rejected_count} undone</span>}
                        {' '}<span className="text-[10px] text-muted-foreground">(±{r.window_days || 3}d)</span>
                      </p>
                      <div className="flex items-center gap-1.5 mt-2">
                        {r.approved ? (
                          <Badge className="text-[10px] bg-success/10 text-success border-success/30" data-testid={`approved-${r.id}`}>
                            <CheckCircle2 size={10} className="mr-1" /> Approved
                          </Badge>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => approveRun(r)} data-testid={`approve-${r.id}`}>
                              <CheckCircle2 size={11} className="mr-1 text-success" /> Approve
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[10px] text-error hover:bg-error/10" onClick={() => rejectRun(r)} data-testid={`reject-${r.id}`}>
                              <XCircle size={11} className="mr-1" /> Undo run
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <p className="text-[11px] text-muted-foreground text-center pt-2">
          Tip: pair this with the <strong>Banking Quick-Link</strong> page to manually link bank txns that auto-match couldn't reach.
        </p>

        {/* iter946 — Cross-Branch Internal Offset approvals */}
        {crossBranch.length > 0 && (
          <Card className="overflow-hidden border-amber-300" data-testid="cross-branch-panel">
            <div className="px-4 py-3 bg-amber-100/70 border-b border-amber-200 flex items-center gap-2">
              <Building2 size={15} className="text-amber-700" />
              <h2 className="text-sm font-semibold text-amber-800">Cross-Branch Matches — Approve Internal Offset</h2>
              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                {crossBranch.filter(s => !s.approved).length} pending
              </Badge>
              <Button
                size="sm" variant="outline"
                className="ml-auto h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-50"
                disabled={approvingAll || crossBranch.filter(s => !s.approved).length === 0}
                onClick={approveAllOffsets}
                data-testid="cross-branch-approve-all"
              >
                {approvingAll ? <Loader2 size={12} className="mr-1 animate-spin" /> : <CheckCircle2 size={12} className="mr-1 text-success" />}
                Approve All
              </Button>
            </div>
            <CardContent className="p-0 divide-y divide-border/40">
              {crossBranch.map(s => {
                const nm = Number(s.name_signal) || 0;
                const nameLabel = nm >= 0.75 ? 'High' : nm >= 0.4 ? 'Med' : nm > 0 ? 'Low' : 'None';
                const dd = Number(s.date_delta_days) || 0;
                return (
                <div key={s.entry_id} className="p-3 flex items-center gap-3" data-testid={`cross-branch-${s.entry_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate" title={s.description}>{s.description}</p>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                      <span className="font-mono">{s.entry_number || s.entry_id?.slice(0, 8)}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1">{(s.from_branch_name || s.from_branch_id?.slice(0, 6))} <ArrowRight size={10} /> {(s.to_branch_name || s.to_branch_id?.slice(0, 6))}</span>
                      <span>·</span>
                      <span>{s.date}</span>
                    </div>
                    {/* iter947 — match-reason chips */}
                    <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid={`cross-branch-reasons-${s.entry_id}`}>
                      <Badge className="text-[9px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">Amount: Match</Badge>
                      <Badge className={`text-[9px] px-1.5 py-0 ${nm >= 0.75 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : nm >= 0.4 ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-stone-100 text-stone-500 border-stone-200'}`}>Name: {nameLabel}</Badge>
                      <Badge className="text-[9px] px-1.5 py-0 bg-violet-50 text-violet-700 border-violet-200">Date: {dd === 0 ? 'Same day' : `+${dd} day${dd === 1 ? '' : 's'}`}</Badge>
                      <Badge className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-800 border-amber-200">Score {s.match_score}</Badge>
                    </div>
                  </div>
                  <span className="font-mono text-xs font-semibold text-amber-800">{cur()} {fmt(s.amount)}</span>
                  {s.approved ? (
                    <Badge className="text-[10px] bg-success/10 text-success border-success/30" data-testid={`cross-branch-approved-${s.entry_id}`}>
                      <CheckCircle2 size={10} className="mr-1" /> Offset created
                    </Badge>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-[10px]"
                      disabled={approvingId === s.entry_id || approvingAll}
                      onClick={() => approveOffset(s)}
                      data-testid={`cross-branch-approve-${s.entry_id}`}
                    >
                      {approvingId === s.entry_id ? <Loader2 size={11} className="mr-1 animate-spin" /> : <CheckCircle2 size={11} className="mr-1 text-success" />}
                      Approve offset
                    </Button>
                  )}
                </div>
              )})}
            </CardContent>
          </Card>
        )}

        {/* Smart Bulk-Link Dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-2xl" data-testid="bulk-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles size={16} className="text-emerald-600" /> Smart bulk-link
              </DialogTitle>
              <DialogDescription className="text-xs">
                Linking <strong>{selectedSummary.count}</strong> bank transaction(s) totaling <strong>{cur()} {fmt(selectedSummary.total)}</strong> to a single entity. Each row gets its own downstream document — same as if you Quick-Linked them one by one, but in one shot.
              </DialogDescription>
            </DialogHeader>

            {groups.length > 0 && groups.length <= 5 && (
              <div className="bg-emerald-50/50 rounded-md p-2 text-[11px] space-y-1 max-h-[100px] overflow-y-auto" data-testid="bulk-grouped-preview">
                <div className="font-semibold text-emerald-700 mb-1">{groups.length} pattern{groups.length === 1 ? '' : 's'} detected:</div>
                {groups.map(g => (
                  <div key={g.key} className="flex items-center justify-between gap-2">
                    <span className="text-foreground truncate">{g.sample}</span>
                    <span className="text-muted-foreground shrink-0">{g.rows.length}× · {cur()} {fmt(g.total)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Entity Type</label>
                <Select value={bulkEntityType} onValueChange={(v) => { setBulkEntityType(v); setBulkEntityId(''); }}>
                  <SelectTrigger className="h-9 text-xs" data-testid="bulk-entity-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map(t => (
                      <SelectItem key={t.key} value={t.key} className="text-xs">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Search</label>
                <Input value={bulkSearch} onChange={e => setBulkSearch(e.target.value)} placeholder="Search…" className="h-9 text-xs" data-testid="bulk-search" />
              </div>
            </div>

            {bulkEntityType !== 'expense_category' ? (
              <div className="max-h-[260px] overflow-y-auto rounded-md border" data-testid="bulk-entity-list">
                {bulkLoading ? (
                  <div className="p-4 text-center text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading…</div>
                ) : filteredBulkEntities.length === 0 ? (
                  <div className="p-4 text-center text-xs text-muted-foreground">No matches</div>
                ) : (
                  filteredBulkEntities.map(e => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => setBulkEntityId(e.id)}
                      className={`w-full text-left px-3 py-2 border-b last:border-b-0 transition-colors flex items-center justify-between ${bulkEntityId === e.id ? 'bg-emerald-100' : 'hover:bg-emerald-50'}`}
                      data-testid={`bulk-entity-${e.id}`}
                    >
                      <div>
                        <div className="text-sm font-semibold">{e.name}</div>
                        {e.sub && <div className="text-[10px] text-muted-foreground">{e.sub}</div>}
                      </div>
                      {bulkEntityId === e.id && <CheckCircle2 size={14} className="text-emerald-600" />}
                    </button>
                  ))
                )}
              </div>
            ) : (
              <>
                {/* Expense category picker — same shape as entities, but with auto-create toggle */}
                <div className="max-h-[200px] overflow-y-auto rounded-md border" data-testid="bulk-entity-list-categories">
                  {bulkLoading ? (
                    <div className="p-4 text-center text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading…</div>
                  ) : filteredBulkEntities.length === 0 ? (
                    <div className="p-4 text-center text-xs text-muted-foreground">No expense categories defined yet</div>
                  ) : (
                    filteredBulkEntities.map(e => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => setBulkEntityId(e.id)}
                        className={`w-full text-left px-3 py-2 border-b last:border-b-0 transition-colors flex items-center justify-between ${bulkEntityId === e.id ? 'bg-emerald-100' : 'hover:bg-emerald-50'}`}
                        data-testid={`bulk-entity-${e.id}`}
                      >
                        <div>
                          <div className="text-sm font-semibold">{e.name}</div>
                          {e.sub && <div className="text-[10px] text-muted-foreground">{e.sub}</div>}
                        </div>
                        {bulkEntityId === e.id && <CheckCircle2 size={14} className="text-emerald-600" />}
                      </button>
                    ))
                  )}
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-2 px-1">
                  <Checkbox
                    checked={autoCreateExpense}
                    onCheckedChange={setAutoCreateExpense}
                    data-testid="auto-create-expense-toggle"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium">Auto-create one expense per txn</span>
                    <p className="text-[10px] text-muted-foreground">
                      {autoCreateExpense
                        ? `Each selected bank txn becomes a new expense (category=selected, amount=txn.amount, payment_mode=bank). Auto-journal fires.`
                        : `Tag-only — bank txns get categorized but no expense doc is created. Useful when you'll create expenses later.`}
                    </p>
                  </div>
                </label>
                {autoCreateExpense && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">
                      Pattern label (used in expense description, optional)
                    </label>
                    <Input
                      value={bulkPattern}
                      onChange={e => setBulkPattern(e.target.value)}
                      placeholder={`e.g. "${groups[0]?.sample?.slice(0, 30) || 'Monthly office rent'}"`}
                      className="h-9 text-xs"
                      data-testid="bulk-pattern"
                    />
                  </div>
                )}
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkSubmitting}>Cancel</Button>
              <Button
                onClick={submitBulkLink}
                disabled={bulkSubmitting || !bulkEntityId}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
                data-testid="bulk-submit-btn"
              >
                {bulkSubmitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
                {bulkEntityType === 'expense_category' && autoCreateExpense
                  ? `Link & create ${selectedSummary.count} expense${selectedSummary.count === 1 ? '' : 's'}`
                  : `Link ${selectedSummary.count} txns`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter — Inline transaction drawer (P0). Reuses existing workflows:
            Add Payment/Expense open the entity dialog above; Link Existing /
            Transfer / Pass-Through deep-link to their validated pages. */}
        <TxnActionDrawer
          txn={drawerTxnResolved}
          open={!!drawerTxn}
          onClose={() => setDrawerTxn(null)}
          onAddPayment={drawerAddPayment}
          onAddExpense={drawerAddExpense}
          onLinkExisting={drawerLinkExisting}
          onTransfer={drawerTransfer}
          onPassThrough={drawerPassThrough}
          onReviewLater={drawerReviewLater}
        />
      </div>
    </DashboardLayout>
  );
}
